import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const STYLES = `
  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .etp-row:hover { background: #f8faff !important; }
  .etp-row.selected { background: #eef2ff !important; }
  .etp-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  .etp-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .etp-tab { transition: all 0.2s; cursor: pointer; }
  .etp-tab.active { background: linear-gradient(135deg,#1A5EA8,#0B2545) !important; color: white !important; }
  .etp-tab:not(.active):hover { background: #eef2ff !important; }
  .etp-edit-btn:hover { background: #eef2ff !important; }
`;

function Badge({ text, color = "#1A5EA8" }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px",
      borderRadius: 999, background: `${color}18`,
      color, border: `1px solid ${color}30`,
      textTransform: "uppercase", letterSpacing: "0.06em"
    }}>{text}</span>
  );
}

function Spinner({ size = 16 }) {
  return <div style={{ width: size, height: size, borderRadius: "50%", border: `2px solid #e5e7eb`, borderTopColor: "#1A5EA8", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />;
}

function TranslationPreview({ json }) {
  try {
    const data = typeof json === "string" ? JSON.parse(json) : json;
    return (
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        {data.summary && <p style={{ color: "#374151", marginBottom: 8, fontStyle: "italic" }}>{data.summary}</p>}
        {data.may_qualify?.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            {data.may_qualify.map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
                <span style={{ color: "#059669", flexShrink: 0 }}>✅</span>
                <span style={{ color: "#374151" }}>{p}</span>
              </div>
            ))}
          </div>
        )}
        {data.may_not_qualify?.length > 0 && (
          <div>
            {data.may_not_qualify.map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
                <span style={{ color: "#dc2626", flexShrink: 0 }}>❌</span>
                <span style={{ color: "#374151" }}>{p}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  } catch {
    return <p style={{ fontSize: 12, color: "#6b7280" }}>{json}</p>;
  }
}

export default function EligibilityTranslationPage() {
  const [tab, setTab] = useState("untranslated");
  const [untranslated, setUntranslated] = useState([]);
  const [translated, setTranslated] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([
        api.get("/v1/admin/translations/untranslated"),
        api.get("/v1/admin/translations/translated"),
      ]);
      setUntranslated(u.data);
      setTranslated(t.data);
      setSelected(new Set());
    } catch (e) {
      showToast("Failed to load trials", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === untranslated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(untranslated.map(t => t.id)));
    }
  };

  const translateSelected = async () => {
    if (selected.size === 0) return;
    setTranslating(true);
    setProgress({ done: 0, total: selected.size });

    const ids = Array.from(selected);
    let done = 0;

    // Translate one at a time to show live progress
    for (const id of ids) {
      try {
        await api.post(`/v1/admin/translations/translate/${id}`);
        done++;
        setProgress({ done, total: ids.length });
      } catch (e) {
        done++;
        setProgress({ done, total: ids.length });
      }
    }

    showToast(`Translated ${done} of ${ids.length} trials successfully`);
    setTranslating(false);
    await loadData();
  };

  const saveEdit = async (trialId) => {
    setSavingEdit(true);
    try {
      await api.put(`/v1/admin/translations/update/${trialId}`, { plainText: editText });
      showToast("Translation updated successfully");
      setEditingId(null);
      setEditText("");
      await loadData();
    } catch (e) {
      showToast("Failed to save translation", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const retranslate = async (trialId) => {
    try {
      await api.post(`/v1/admin/translations/translate/${trialId}`);
      showToast("Re-translated successfully");
      await loadData();
    } catch (e) {
      showToast("Failed to re-translate", "error");
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
      <style>{STYLES}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
          borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 600,
          color: toast.type === "error" ? "#dc2626" : "#059669",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          animation: "fadeIn 0.3s ease",
        }}>
          {toast.type === "error" ? "❌" : "✅"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 700, color: "#0D2B45", margin: "0 0 6px" }}>
          Eligibility Translation Manager
        </h1>
        <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
          Translate complex eligibility criteria into plain English for patients
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        {[
          { label: "Untranslated", value: untranslated.length, color: "#e05c3a" },
          { label: "Translated",   value: translated.length,   color: "#059669" },
          { label: "Total Trials", value: untranslated.length + translated.length, color: "#1A5EA8" },
          { label: "Coverage",     value: `${translated.length + untranslated.length > 0 ? Math.round((translated.length / (translated.length + untranslated.length)) * 100) : 0}%`, color: "#0B2545" },
        ].map(s => (
          <div key={s.label} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 24px", flex: "1 1 120px", minWidth: 120 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 4, marginBottom: 20, width: "fit-content" }}>
        {[
          { id: "untranslated", label: `🔄 Needs Translation (${untranslated.length})` },
          { id: "translated",   label: `✅ Translated (${translated.length})` },
        ].map(t => (
          <button key={t.id} className={`etp-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
            style={{ padding: "8px 18px", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: "transparent", color: "#6b7280", cursor: "pointer",
              fontFamily: "'DM Sans', system-ui, sans-serif" }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={32} /></div>
      ) : tab === "untranslated" ? (

        // ── UNTRANSLATED TAB ────────────────────────────────────────────────
        <div>
          {/* Toolbar */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <button className="etp-btn"
              onClick={toggleSelectAll}
              style={{ padding: "8px 16px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#374151", transition: "all 0.2s" }}>
              {selected.size === untranslated.length && untranslated.length > 0 ? "Deselect All" : "Select All"}
            </button>

            <button className="etp-btn"
              onClick={translateSelected}
              disabled={selected.size === 0 || translating}
              style={{ padding: "8px 20px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s", boxShadow: selected.size > 0 ? "0 4px 14px rgba(102,126,234,0.4)" : "none" }}>
              {translating ? <><Spinner size={14} /> Translating...</> : `✨ Translate Selected (${selected.size})`}
            </button>

            {translating && (
              <div style={{ fontSize: 13, color: "#1A5EA8", fontWeight: 600 }}>
                {progress.done} / {progress.total} complete
                <div style={{ width: 120, height: 4, background: "#e5e7eb", borderRadius: 999, marginTop: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", background: "linear-gradient(90deg,#1A5EA8,#0B2545)", borderRadius: 999, width: `${(progress.done / progress.total) * 100}%`, transition: "width 0.3s" }} />
                </div>
              </div>
            )}

            <span style={{ fontSize: 13, color: "#9ca3af", marginLeft: "auto" }}>
              {selected.size} of {untranslated.length} selected
            </span>
          </div>

          {untranslated.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <p style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>All trials have been translated!</p>
              <p style={{ fontSize: 14 }}>Switch to the Translated tab to review and edit.</p>
            </div>
          ) : (
            <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
              {/* Table header */}
              <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 160px 120px 80px", gap: 0,
                background: "#0D2B45", padding: "12px 16px", fontSize: 11, fontWeight: 700,
                color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <div />
                <div>Trial</div>
                <div>Sponsor</div>
                <div>Status</div>
                <div style={{ textAlign: "center" }}>Translate</div>
              </div>

              {untranslated.map((trial, i) => (
                <div key={trial.id}>
                  <div className={`etp-row ${selected.has(trial.id) ? "selected" : ""}`}
                    style={{ display: "grid", gridTemplateColumns: "40px 1fr 160px 120px 80px", gap: 0,
                      padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
                      background: selected.has(trial.id) ? "#eef2ff" : i % 2 === 0 ? "#fafafa" : "white",
                      transition: "background 0.15s" }}>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input type="checkbox" checked={selected.has(trial.id)}
                        onChange={() => toggleSelect(trial.id)}
                        style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#1A5EA8" }} />
                    </div>

                    <div style={{ paddingRight: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0D2B45", marginBottom: 2 }}>{trial.title}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{trial.nctId}</div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>
                        {trial.eligibilityCriteria}
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center" }}>{trial.sponsor}</div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Badge text={trial.status || "Unknown"} color={trial.status === "RECRUITING" ? "#059669" : "#6b7280"} />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <button className="etp-btn"
                        onClick={() => retranslate(trial.id)}
                        style={{ fontSize: 11, fontWeight: 600, padding: "5px 10px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                        ✨ AI
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      ) : (

        // ── TRANSLATED TAB ──────────────────────────────────────────────────
        <div>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px 160px", gap: 0,
              background: "#0D2B45", padding: "12px 16px", fontSize: 11, fontWeight: 700,
              color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              <div>Trial</div>
              <div>Sponsor</div>
              <div>Translated By</div>
              <div>Actions</div>
            </div>

            {translated.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                <p>No translations yet. Go to the "Needs Translation" tab to get started.</p>
              </div>
            ) : translated.map((trial, i) => (
              <div key={trial.id}>
                <div className="etp-row"
                  style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px 160px", gap: 0,
                    padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
                    background: i % 2 === 0 ? "#fafafa" : "white", transition: "background 0.15s" }}>

                  <div style={{ paddingRight: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0D2B45", marginBottom: 2 }}>{trial.title}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>{trial.nctId}</div>
                    <button className="etp-edit-btn"
                      onClick={() => setExpandedId(expandedId === trial.id ? null : trial.id)}
                      style={{ fontSize: 11, color: "#1A5EA8", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, fontFamily: "inherit" }}>
                      {expandedId === trial.id ? "▲ Hide translation" : "▼ Preview translation"}
                    </button>
                  </div>

                  <div style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "flex-start", paddingTop: 2 }}>{trial.sponsor}</div>

                  <div style={{ fontSize: 11, color: "#6b7280", display: "flex", alignItems: "flex-start", paddingTop: 2 }}>
                    {trial.translatedBy === "SYSTEM" ? "🤖 AI" : `👤 ${trial.translatedBy}`}
                    {trial.translatedAt && (
                      <span style={{ display: "block", fontSize: 10, color: "#9ca3af" }}>
                        {new Date(trial.translatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 6, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <button className="etp-btn etp-edit-btn"
                      onClick={() => { setEditingId(trial.id); setEditText(trial.eligibilityPlainText); }}
                      style={{ fontSize: 11, fontWeight: 600, padding: "5px 10px", background: "#eef2ff", color: "#1A5EA8", border: "1px solid #c7d2fe", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                      ✏️ Edit
                    </button>
                    <button className="etp-btn"
                      onClick={() => retranslate(trial.id)}
                      style={{ fontSize: 11, fontWeight: 600, padding: "5px 10px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                      ✨ Re-AI
                    </button>
                  </div>
                </div>

                {/* Expanded preview */}
                {expandedId === trial.id && (
                  <div style={{ padding: "16px 24px 16px 16px", background: "#f8faff", borderBottom: "1px solid #e5e7eb", animation: "fadeIn 0.2s ease" }}>
                    <TranslationPreview json={trial.eligibilityPlainText} />
                  </div>
                )}

                {/* Edit panel */}
                {editingId === trial.id && (
                  <div style={{ padding: "16px", background: "#fffbeb", borderBottom: "1px solid #fde68a", animation: "fadeIn 0.2s ease" }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: "#92400e", marginBottom: 8 }}>
                      ✏️ Edit Translation — changes will be attributed to you
                    </p>
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      rows={8}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 12, fontFamily: "monospace", resize: "vertical", outline: "none", background: "white" }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button className="etp-btn"
                        onClick={() => saveEdit(trial.id)}
                        disabled={savingEdit}
                        style={{ padding: "8px 18px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s" }}>
                        {savingEdit ? <><Spinner size={12} /> Saving...</> : "💾 Save"}
                      </button>
                      <button className="etp-btn"
                        onClick={() => { setEditingId(null); setEditText(""); }}
                        style={{ padding: "8px 18px", background: "white", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}